import { useState, useEffect, useRef } from "react";
import { api, handleAuthFailure } from "../data/api";
import { generateDeviceId } from "../data/ids";
import { isMultiChannelDevice, maxChannelsForDevice } from "../data/deviceModels";
import Badge from "../components/Badge";
import Pill from "../components/Pill";
import StatCard from "../components/StatCard";
import DeleteConfirm from "../components/DeleteConfirm";
import EditAffiliate from "./EditAffiliate";
import Btn from "../components/Btn";
import Input from "../components/Input";
import { TableWrap, TH, TD } from "../components/TableWrap";
import AffiliateViewer from "../components/AffiliateViewer";
import StatusToggle from "../components/StatusToggle";
import { useTheme } from "../context/ThemeContext";
import { Eye, Pencil } from "lucide-react";
import DeviceCombobox from "../components/DeviceCombobox";
import * as XLSX from 'xlsx';

const EMPTY_FORM = {
  id: "",
  org: "",
  contact: "",
  email: "",
  region: "",
  tz: "GMT",
  channels: [],
  devices: [], // array of { mac, model, channelId, isNew }
};

const validMAC = (mac) =>
  /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/.test(mac.trim());

const STEPS = [
  "Basic Info",
  "Channel Selection",
  "Device Configuration",
  "Review & Confirm",
];

export default function AffiliateManagement({ readOnly = false }) {
  const { mode } = useTheme();
  const [affiliates, setAffiliates] = useState([]);
  const [channels, setChannels] = useState([]);
  const [takenChannels, setTakenChannels] = useState([]);
  const [deviceModels, setDeviceModels] = useState([]);
  const [existingDevices, setExistingDevices] = useState([]); // devices already in DB
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult]       = useState(null);
  const [bulkDragOver, setBulkDragOver]   = useState(false);
  const bulkInputRef = useRef(null);
  const [step, setStep] = useState(1);
  const [filterStatus, setFilterStatus] = useState("all");
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [viewingId, setViewingId] = useState(null);
   const [affSearch, setAffSearch]         = useState("");

  // ── Bulk upload from eBox worksheet ──────────────────────────────────────
  // Reads the real eBox Worksheet column layout:
  //   Customer code | Location code | Active status | MSO | Affiliate Name |
  //   Affiliate Id (if applicable) | Affiliate Organization Name (if applicable) |
  //   Affiliate Contact Name | Affiliate Contact Email | Affiliate Contact Phone Number |
  //   Affiliate Region | IP Type (Static/Dynamic) | IP Address (if Static) |
  //   Channel Names (SD/HD ) | E-box MAC Addresses | E-box Location | Channel Numbers
  //
  // Grouping: rows with the same Affiliate Name (trimmed) are merged into one
  // affiliate record.  Customer Code is stored as the affiliate ID prefix but
  // rows for the same physical operator that arrive under different Customer
  // Codes (e.g. 1214199TEN vs 1214199SPN) are unified by Affiliate Name so
  // the operator gets a single record with all their devices.
  const handleBulkUpload = async (file) => {
    if (!file) return;
    setBulkUploading(true);
    setBulkResult(null);
    const log = { affiliates: [], devices: [], channelFlags: [], skipped: [], errors: [] };

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      // Use array mode so every cell is present even if blank — sheet_to_json
      // with object mode silently omits keys for empty cells, breaking contact
      // name lookups on rows where the cell exists but is empty.
      const [headerRow, ...dataRows] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      if (!headerRow || !dataRows.length) throw new Error('File is empty or could not be read.');

      // Normalise headers once
      const headers = headerRow.map(h => String(h ?? '').toLowerCase().trim());

      // Build objects with guaranteed keys for every header column
      const data = dataRows.map(row =>
        Object.fromEntries(headers.map((h, i) => [h, String(row[i] ?? '').trim()]))
      ).filter(r => Object.values(r).some(v => v !== '')); // drop fully-blank rows

      // Fetch existing channels from DB
      // Use the channels already loaded into component state — no extra API call needed
      const existingChannels = channels;

      // ── Channel alias table ────────────────────────────────────────────────
      // ONLY the 12 channels that exist in the DB are listed here.
      // ORDER MATTERS for the partial-match fallback — more specific aliases
      // must appear before shorter ones that could accidentally swallow them
      // (e.g. 'ten 1' must come before 'ten' would if it existed, and 'sony yay!'
      // before 'yay', so exact matches are tried first and partial matches
      // never steal a more-specific hit).
      const CHANNEL_ALIASES = {
        'sony yay!-bb': 'ch-223', 'sony yay!': 'ch-223', 'sony yay': 'ch-223', 'yay': 'ch-223',
        'sony aath-bb': 'ch-227', 'sony aath': 'ch-227', 'aath': 'ch-227',
        'ten 2-bb': 'ch-228', 'ten 2': 'ch-228', 'ten2': 'ch-228',
        'ten 5-bb': 'ch-230', 'ten 5': 'ch-230', 'ten5': 'ch-230',
        'max 2-bb': 'ch-231', 'max 2': 'ch-231', 'max2': 'ch-231',
        'ten 5 bangali-bb': 'ch-233', 'ten 5 bangali': 'ch-233', 'ten 5 bangla': 'ch-233',
        'sony max-bb': 'ch-236', 'sony max': 'ch-236', 'max': 'ch-236',
        'sony ten 1-bb': 'ch-237', 'sony ten 1': 'ch-237', 'ten 1': 'ch-237', 'ten1': 'ch-237',
        'set-bb': 'ch-238', 'sony set': 'ch-238', 'sony ent': 'ch-238', 'set': 'ch-238',
        'sony pix-bb': 'ch-239', 'sony pix': 'ch-239', 'pix': 'ch-239',
        'sony sab-bb': 'ch-240', 'sony sab': 'ch-240', 'sab': 'ch-240',
        'sony bbc earth-bb': 'ch-241', 'sony bbc earth': 'ch-241', 'bbc earth': 'ch-241',
      };

      const findChannel = (sheetName) => {
        if (!sheetName) return null;
        const needle = sheetName.toLowerCase().trim();
        const aliasId = CHANNEL_ALIASES[needle];
        if (aliasId) {
          const ch = existingChannels.find(c => c.id === aliasId) || null;
          console.log(`[findChannel] "${sheetName}" -> exact alias ${aliasId} -> db match: ${ch ? ch.display_name : 'NOT IN DB'}`);
          return ch;
        }
        const candidates = Object.entries(CHANNEL_ALIASES)
          .filter(([alias]) => alias.includes(needle) || needle.includes(alias))
          .sort((a, b) => b[0].length - a[0].length);
        if (candidates.length) {
          const [, chId] = candidates[0];
          const ch = existingChannels.find(c => c.id === chId) || null;
          console.log(`[findChannel] "${sheetName}" -> partial match ${chId} -> db match: ${ch ? ch.display_name : 'NOT IN DB'}`);
          return ch;
        }
        console.log(`[findChannel] "${sheetName}" -> NO MATCH (needle="${needle}")`);
        return null;
      };
      console.log('[BulkUpload] existingChannels from DB:', existingChannels.map(c => c.id + ':' + c.display_name));

      const getOrgName = (row) =>
        row['affiliate organization name (if applicable)'] ||
        row['affiliate name'] ||
        row['mso'] ||
        '';

      const existingOrgNames = new Set(
        affiliates.map(a =>
          ((a.org_name || a.name || '') + '|' + (a.contact_name || '') + '|' + (a.contact_email || '')).toLowerCase().trim()
        ).filter(Boolean)
      );
      const existingAffiliateIds = new Set(
        affiliates.map(a => (a.id || '').toLowerCase().trim()).filter(Boolean)
      );
      const existingMACs = new Set(
        existingDevices.map(d => (d.mac_address || '').toUpperCase().trim()).filter(Boolean)
      );

      const affiliateMap = {};

      for (const row of data) {
        const customerCode = row['customer code'] || '';
        const orgName      = getOrgName(row);
        if (!orgName) continue;

        const contactName  = row['affiliate contact name']  || '';
        const contactEmail = row['affiliate contact email'] || '';
        const orgKey = (orgName + '|' + contactName + '|' + contactEmail).toLowerCase().trim();

        if (!affiliateMap[orgKey]) {
          affiliateMap[orgKey] = {
            customerCode,
            orgName,
            contactName,
            contactEmail,
            contactPhone: row['affiliate contact phone number'] || '',
            region:       row['affiliate region']        || '',
            activeStatus: row['active status']           || '',
            devices: [],
          };
        }

        const macRaw = row['e-box mac addresses'] || row['mac address'] || '';
        const macClean = macRaw.replace(/[^A-Fa-f0-9]/g, '');
        const mac = macClean.length === 12
          ? macClean.match(/.{1,2}/g).join(':').toUpperCase()
          : '';

        const channelName  = row['channel names (sd/hd )'] || row['channel names (sd/hd)'] || row['channel names'] || '';
        const ipAddress    = row['ip address (if static)']  || null;
        const ipType       = (row['ip type (static/dynamic)'] || '').toLowerCase().includes('static') ? 'static' : 'dhcp';
        const eboxLocation = row['e-box location'] || '';
        const locationCode = row['location code']  || '';

        if (mac) {
          affiliateMap[orgKey].devices.push({ mac, channelName, ipAddress, ipType, eboxLocation, locationCode, customerCode });
        }
      }

      const parsedAffiliates = Object.values(affiliateMap);
      if (!parsedAffiliates.length) throw new Error('No valid rows found. Check the file uses the standard eBox worksheet format.');
      console.log('[BulkUpload] parsed affiliates:', parsedAffiliates.map(a => ({
        org: a.orgName,
        devices: a.devices.map(d => ({ mac: d.mac, ch: d.channelName }))
      })));

      for (const aff of parsedAffiliates) {
        const orgKey = (aff.orgName + '|' + aff.contactName + '|' + aff.contactEmail).toLowerCase().trim();
        if (existingOrgNames.has(orgKey)) {
          log.skipped.push({ name: aff.orgName });
          continue;
        }

        const validDevices = [];
        for (const dev of aff.devices) {
          const matchedCh = findChannel(dev.channelName);
          if (!matchedCh) {
            // Unknown channel — skip this row only, flag it
            if (dev.channelName && !log.channelFlags.some(f => f.name === dev.channelName)) {
              log.channelFlags.push({ name: dev.channelName, affiliate: aff.orgName });
            }
          } else {
            validDevices.push({ ...dev, matchedCh });
          }
        }

        if (validDevices.length === 0) {
          // Every device row had an unrecognised channel — skip affiliate too
          log.skippedAffiliates = log.skippedAffiliates || [];
          log.skippedAffiliates.push({ name: aff.orgName, reason: 'All channels unrecognised' });
          continue;
        }

        const contactSlug = aff.contactName.replace(/[^a-zA-Z]/g, '').slice(0, 6).toLowerCase();
        const orgSlug = (aff.orgName.replace(/[^a-z0-9]/g, '') + contactSlug).slice(0, 20);
        let affId = orgSlug ? `aff-${orgSlug}` : 'aff-' + Math.random().toString(16).slice(2, 10);
        if (existingAffiliateIds.has(affId.toLowerCase())) {
          affId = affId + '-' + Math.random().toString(16).slice(2, 6);
        }
        try {
          await api.affiliates.create({
            id: affId, org_name: aff.orgName, contact_name: aff.contactName,
            contact_email: aff.contactEmail || '', region: aff.region || '',
            timezone: '', status: 'active',
          });
          log.affiliates.push({ id: affId, name: aff.orgName });
          existingOrgNames.add(orgKey);
          existingAffiliateIds.add(affId.toLowerCase());
        } catch (e) {
          const msg = (e.message || '').toLowerCase();
          if (msg.includes('already exists') || msg.includes('duplicate') || msg.includes('unique') || msg.includes('conflict')) {
            log.skipped.push({ name: aff.orgName });
          } else {
            log.errors.push({ name: aff.orgName, message: e.message });
          }
          continue;
        }

        for (const dev of validDevices) {
          const { matchedCh } = dev;
          const channelId = matchedCh.id;

          if (existingMACs.has(dev.mac)) {
            log.skipped.push({ name: dev.mac, type: 'device' });
            continue;
          }

          try { await api.affiliates.assignChannels(affId, [channelId]); } catch (_) {}

          const devId = generateDeviceId();
          try {
            await api.devices.create({
              id: devId, mac_address: dev.mac, ip_address: dev.ipAddress,
              ip_type: dev.ipType, device_model: null, app_version: '—',
              affiliate_id: affId,
              location: dev.eboxLocation || null,
            });
            log.devices.push({ id: devId, mac: dev.mac, channel: dev.channelName });
            existingMACs.add(dev.mac);

            try {
              await api.devices.assignChannel(devId, {
                channel_id:   channelId,
                channel_url:  matchedCh.master_srt_url || '',
                display_port: 1,
                audio_device: 'default',
              });
            } catch (assignErr) {
              log.errors.push({ name: dev.mac, message: `Channel assign failed: ${assignErr.message}` });
            }
          } catch (e) {
            log.errors.push({ name: dev.mac, message: e.message });
          }
        }
      }

      setBulkResult(log);
      if (log.affiliates.length > 0 || log.devices.length > 0) await load();

    } catch (e) {
      setBulkResult({ affiliates: [], devices: [], channelFlags: [], skipped: [], errors: [{ name: '—', message: e.message }] });
    } finally {
      setBulkUploading(false);
      if (bulkInputRef.current) bulkInputRef.current.value = '';
    }
  };
  const load = async () => {
    setLoading(true);
    try {
      const [affs, chs, taken, models, devs] = await Promise.all([
        api.affiliates.list(),
        api.channels.list().catch(() => []),
        api.devices.takenChannels().catch(() => []),
        api.devices.models().catch(() => []),
        api.devices.list({}).catch(() => []),
      ]);
      setAffiliates(affs);
      setChannels(chs);
      setTakenChannels(taken);
      setDeviceModels(models);
      setExistingDevices(devs);
    } catch (e) {
      if (handleAuthFailure(e)) return;
      setError("Failed to load data. Is the server running?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const f = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Parent/child hierarchy lookups
  const affById = Object.fromEntries(affiliates.map(a => [a.id, a]));
  const childCountByParent = {};
  for (const a of affiliates) {
    if (a.parent_id) childCountByParent[a.parent_id] = (childCountByParent[a.parent_id] || 0) + 1;
  }

  const filtered = affiliates
    .filter((a) => filterStatus === "all" ? a.status !== "archived" : a.status === filterStatus)
    .filter((a) => {
      if (!affSearch.trim()) return true;
      const q = affSearch.toLowerCase();
      const parentName = a.parent_id ? (affById[a.parent_id]?.org_name || '') : '';
      return (
        a.org_name?.toLowerCase().includes(q) ||
        a.id?.toLowerCase().includes(q) ||
        a.contact_name?.toLowerCase().includes(q) ||
        a.region?.toLowerCase().includes(q) ||
        parentName.toLowerCase().includes(q)
      );
    });

  const handleBack = () => {
    setShowNew(false);
    setStep(1);
    setForm(EMPTY_FORM);
    setError(null);
  };

  const sessionUsedChannels = form.devices
    .map((d) => d.channelId)
    .filter(Boolean);

  // A channel can legitimately go out to many affiliates/devices at once —
  // 'taken' globally just means SOME other device already carries it, which
  // is informational, not a reason to block picking it here. The only real
  // block is picking the same channel twice for two different devices being
  // created in THIS wizard, since each new device needs its own channel.
  // Channels already picked by OTHER device rows of the same device type
  // (box vs self-srt). A channel may appear once per type per affiliate, so
  // rows of a different type never block each other.
  const channelsPickedByType = (excludeIndex, type) =>
    form.devices
      .filter((_, i) => i !== excludeIndex)
      .filter((d) => (d.deviceType || "box") === type)
      .flatMap((d) =>
        Array.isArray(d.channelIds)
          ? d.channelIds
          : d.channelId
            ? [d.channelId]
            : [],
      );

  const isChannelBlocked = (chId, currentDeviceIndex) => {
    const type = form.devices[currentDeviceIndex]?.deviceType || "box";
    return channelsPickedByType(currentDeviceIndex, type).includes(chId);
  };
  const isChannelInUseElsewhere = (chId) => takenChannels.includes(chId);

  const handleDelete = async (id) => {
    await api.affiliates.delete(id);
    setDeletingId(null);
    await load();
  };

  const addDeviceRow = () => {
    setForm((p) => ({
      ...p,
      devices: [
        ...p.devices,
        {
          isNew: true,
          deviceType: "box",
          mac: "",
          model: deviceModels[0] || "",
          channelId: "",
          channelIds: [],
          ipType: "dhcp",
          staticIp: "",
          dhcpIp: "",
        },
      ],
    }));
  };

  const removeDeviceRow = (index) => {
    setForm((p) => ({
      ...p,
      devices: p.devices.filter((_, i) => i !== index),
    }));
  };

  const handleStatusChange = async (id, newStatus) => {
    await api.affiliates.updateStatus(id, newStatus);
    await load(); // refresh list from DB
  };

  const setDeviceField = (index, field, value) => {
    setForm((p) => {
      const devices = [...p.devices];
      devices[index] = { ...devices[index], [field]: value };
      return { ...p, devices };
    });
  };

  const selectExistingDevice = (index, deviceId) => {
    if (!deviceId) {
      setDeviceField(index, "mac", "");
      setDeviceField(index, "model", deviceModels[0] || "");
      setDeviceField(index, "deviceType", "box");
      setDeviceField(index, "isNew", true);
      return;
    }
    const dev = existingDevices.find((d) => d.id === deviceId);
    if (!dev) return;
    setForm((p) => {
      const devices = [...p.devices];
      devices[index] = {
        ...devices[index],
        deviceType: dev.device_type || "box",
        mac: dev.mac_address,
        model: dev.device_model,
        staticIp: dev.ip_address || "",
        isNew: false,
        existingId: dev.id,
      };
      return { ...p, devices };
    });
  };

  const syncDevicesFromChannels = () => {
    const assignedChannels = form.devices
      .map((d) => d.channelId)
      .filter(Boolean);
    const unassigned = form.channels.filter(
      (chId) => !assignedChannels.includes(chId),
    );
    const newRows = unassigned.map((chId) => ({
      deviceType: "box",
      mac: "",
      model: deviceModels[0] || "",
      channelId: chId,
      isNew: true,
    }));
    const kept = form.devices.filter(
      (d) => !d.channelId || form.channels.includes(d.channelId),
    );
    setForm((p) => ({ ...p, devices: [...kept, ...newRows] }));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.id.trim()) return "Affiliate ID is required";
      if (!form.org.trim()) return "Organization name is required";
      if (!form.contact.trim()) return "Contact name is required";
      if (!form.email.trim()) return "Contact email is required";
    }
    if (step === 3) {
      if (form.devices.length === 0) return null; // allowed to skip devices
      for (let i = 0; i < form.devices.length; i++) {
        const dev = form.devices[i];
        if (dev.deviceType === "self-srt-connect") {
          const ip = dev.staticIp?.trim();
          if (!ip) return `Device ${i + 1}: IP address is required for Self-SRT-Connect`;
          if (!/^(\d{1,3}\.){3}\d{1,3}$/.test(ip)) return `Device ${i + 1}: Enter a valid IPv4 address`;
        } else {
          if (!dev.mac?.trim()) return `Device ${i + 1}: MAC address is required`;
          if (!validMAC(dev.mac)) return `Device ${i + 1}: Invalid MAC — use XX:XX:XX:XX:XX:XX`;
          if (!dev.model?.trim()) return `Device ${i + 1}: Device model is required`;
        }
        // 3568 uses channelIds array; all others use channelId
        const hasChannel = isMultiChannelDevice(dev)
          ? (Array.isArray(dev.channelIds) ? dev.channelIds.length > 0 : !!dev.channelId)
          : !!dev.channelId;
        if (!hasChannel) return `Device ${i + 1}: Please select a channel`;
      }
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    if (step === 2) syncDevicesFromChannels();
    setStep((s) => s + 1);
  };

  const goBack = () => {
    setError(null);
    setStep((s) => s - 1);
  };

  if (editingId)
    return (
      <EditAffiliate
        affiliateId={editingId}
        onBack={(navigateToId) => setEditingId(navigateToId || null)}
        onSaved={() => load()}
      />
    );

  const handlePurge = async () => {
    setPurging(true);
    try {
      const result = await api.affiliates.purge();
      setPurgeResult(
        result.purged === 0
          ? "No affiliates eligible for purge yet."
          : `Purged ${result.purged} archived affiliate${result.purged !== 1 ? "s" : ""}.`,
      );
      setTimeout(() => setPurgeResult(null), 4000);
      await load();
    } catch (e) {
      setPurgeResult("Purge failed: " + e.message);
    } finally {
      setPurging(false);
    }
  };

  const handleCreate = async () => {
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // 1. Create affiliate
      const created = await api.affiliates.create({
        id: form.id.trim(),
        org_name: form.org.trim(),
        contact_name: form.contact.trim(),
        contact_email: form.email.trim(),
        region: form.region.trim(),
        timezone: form.tz.trim(),
      });

      // 2. Assign channels to affiliate — include step-2 selections AND device channels
      const allChannelIds = [
        ...new Set([
          ...form.channels,
          ...form.devices.map((d) => d.channelId).filter(Boolean),
        ]),
      ];
      if (allChannelIds.length > 0) {
        await api.affiliates.assignChannels(created.id, allChannelIds);
      }

      // 3. Register each device and assign channel(s)
      for (let i = 0; i < form.devices.length; i++) {
        const dev = form.devices[i];
        const chIds = isMultiChannelDevice(dev)
          ? (Array.isArray(dev.channelIds) ? dev.channelIds : (dev.channelId ? [dev.channelId] : []))
          : (dev.channelId ? [dev.channelId] : []);
        if (chIds.length === 0) continue;

        let deviceId;

        if (dev.isNew) {
          const isSelfSrt = dev.deviceType === "self-srt-connect";
          const newDev = await api.devices.create({
            id: generateDeviceId(),
            device_type: isSelfSrt ? "self-srt-connect" : "box",
            mac_address: isSelfSrt ? null : dev.mac.trim().toUpperCase(),
            device_model: isSelfSrt ? null : dev.model,
            affiliate_id: created.id,
            ip_type: isSelfSrt ? "static" : (dev.ipType || "dhcp"),
            ip_address: isSelfSrt
              ? dev.staticIp.trim()
              : (dev.ipType === "static" ? dev.staticIp : null),
          });
          deviceId = newDev.id;
        } else {
          deviceId = dev.existingId;
          await api.devices.patch(deviceId, { affiliate_id: created.id });
        }

        for (const chId of chIds) {
          const ch = channels.find(c => c.id === chId);
          await api.devices.assignChannel(deviceId, {
            channel_id: chId,
            channel_url: ch?.master_srt_url || '',
            display_port: 1,
            audio_device: 'default',
          });
        }
      }

      setForm(EMPTY_FORM);
      setStep(1);
      setShowNew(false);
      await load();
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Stepper ──────────────────────────────────────────────────────────────────
  const Stepper = () => (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 22 }}>
      {STEPS.map((s, i) => {
        const done = step > i + 1,
          cur = step === i + 1;
        return (
          <div key={s} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: done || cur ? "var(--ink)" : "var(--surface-alt)",
                  border: `1.5px solid ${done || cur ? "var(--ink)" : "var(--border)"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  color: done || cur ? "#fff" : "var(--text-dim)",
                }}
              >
                {done ? "✓" : i + 1}
              </div>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: cur ? 600 : 400,
                  color: cur ? "var(--ink)" : "var(--text-sub)",
                }}
              >
                {s}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  width: 22,
                  height: 1,
                  background: "var(--border)",
                  margin: "0 8px",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  // ── Wizard ───────────────────────────────────────────────────────────────────
  if (showNew)
    return (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 18,
            paddingBottom: 14,
            borderBottom: `1px solid var(--border)`,
          }}
        >
          <h1 style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>
            New Affiliate Setup
          </h1>
          <Btn onClick={handleBack}>← Back to Affiliates</Btn>
        </div>

        <Stepper />

        {error && (
          <div
            style={{
              background: "var(--red-bg)",
              border: `1px solid var(--red-border)`,
              borderRadius: 7,
              padding: "10px 14px",
              marginBottom: 14,
              fontSize: 12,
              color: "var(--red)",
            }}
          >
            {error}
          </div>
        )}

        {/* ── Step 1: Basic Info ─────────────────────────────────────────────── */}
        {step === 1 && (
          <div
            style={{
              background: "var(--surface)",
              border: `1px solid var(--border)`,
              borderRadius: 9,
              padding: 22,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
              }}
            >
             {[
                ["Affiliate ID*", "id"],
                ["Organization Name*", "org"],
                ["Contact Name*", "contact"],
                ["Contact Email*", "email"],
                ["Timezone", "tz"],
              ].map(([lbl, k]) => (
                <Input
                  key={k}
                  label={lbl}
                  value={form[k]}
                  onChange={f(k)}
                />
              ))}

              <div>
                <div style={{ fontSize:11, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:600, marginBottom:5 }}>
                  Region
                </div>
                <select
                  value={form.region}
                  onChange={f("region")}
                  style={{ width:"100%", boxSizing:"border-box", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"7px 10px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
                >
                  <option value="">— Select region —</option>
                  <option value="Barishal">Barishal</option>
                  <option value="Chattogran">Chattogran</option>
                  <option value="Dhaka">Dhaka</option>
                  <option value="Khulna">Khulna</option>
                  <option value="Mymensingh">Mymensingh</option>
                  <option value="Rajshahi">Rajshahi</option>
                  <option value="Rangpur">Rangpur</option>
                  <option value="Sylhet">Sylhet</option>
                </select>
              </div>
            </div>
            <div
              style={{
                marginTop: 18,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Btn primary onClick={goNext}>
                Next →
              </Btn>
            </div>
          </div>
        )}

        {/* ── Step 2: Channel Selection ──────────────────────────────────────── */}
        {step === 2 && (
          <div
            style={{
              background: "var(--surface)",
              border: `1px solid var(--border)`,
              borderRadius: 9,
              padding: 22,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text-dim)",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                marginBottom: 6,
              }}
            >
              Select Channels
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--text-sub)",
                marginBottom: 16,
              }}
            >
              Select which channels this affiliate can receive. Each selected
              channel will need one device assigned in the next step.
            </div>

            {channels.length === 0 ? (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text-dim)",
                  padding: "20px 0",
                }}
              >
                No channels available. Create a channel first.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                }}
              >
                {channels.map((ch) => {
                  const sel = form.channels.includes(ch.id);
                  const inUse = takenChannels.includes(ch.id) && !sel;

                  return (
                    <div
                      key={ch.id}
                      onClick={() => {
                        setForm((p) => ({
                          ...p,
                          channels: sel
                            ? p.channels.filter((c) => c !== ch.id)
                            : [...p.channels, ch.id],
                        }));
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "11px 13px",
                        borderRadius: 7,
                        border: `1px solid ${sel ? "var(--border-strong)" : "var(--border)"}`,
                        background: sel
                          ? "var(--surface-alt)"
                          : "var(--surface)",
                        cursor: "pointer",
                        transition: "all 0.12s",
                      }}
                    >
                      <div
                        style={{
                          width: 15,
                          height: 15,
                          borderRadius: 4,
                          border: `1.5px solid ${sel ? "var(--ink)" : "var(--border)"}`,
                          background: sel ? "var(--ink)" : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {sel && (
                          <span
                            style={{
                              color: "#fff",
                              fontSize: 9,
                              fontWeight: 900,
                              lineHeight: 1,
                            }}
                          >
                            ✓
                          </span>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "var(--ink)",
                            fontFamily: "'IBM Plex Mono', monospace",
                          }}
                        >
                          {ch.id}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-sub)" }}>
                          {ch.display_name}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 3,
                        }}
                      >
                        <Badge status={ch.status} />
                        {inUse && (
                          <span
                            style={{
                              fontSize: 10,
                              color: "var(--text-dim)",
                              fontWeight: 500,
                            }}
                          >
                            In use
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div
              style={{
                marginTop: 18,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Btn onClick={goBack}>← Back</Btn>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn
                  onClick={() => {
                    setError(null);
                    setStep(3);
                    syncDevicesFromChannels();
                  }}
                >
                  Skip
                </Btn>
                <Btn primary onClick={goNext}>
                  Next →
                </Btn>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3: Device Configuration ──────────────────────────────────── */}
        {step === 3 && (
          <div
            style={{
              background: "var(--surface)",
              border: `1px solid var(--border)`,
              borderRadius: 9,
              padding: 22,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text-dim)",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                marginBottom: 6,
              }}
            >
              Device Configuration
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--text-sub)",
                marginBottom: 18,
              }}
            >
              Each device receives exactly one channel. Enter a MAC address or
              select an existing device, then assign a channel.
            </div>

            {/* Device rows */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                marginBottom: 14,
              }}
            >
              {form.devices.length === 0 && (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text-dim)",
                    padding: "16px 0",
                  }}
                >
                  No devices added yet. Click "+ Add Device" below.
                </div>
              )}

              {form.devices.map((dev, i) => {
                const usedChannelInRow = dev.channelId;
                return (
                  <div
                    key={i}
                    style={{
                      border: `1px solid var(--border)`,
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    {/* Row header */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "9px 14px",
                        background: "var(--surface-alt)",
                        borderBottom: `1px solid var(--border)`,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--ink)",
                        }}
                      >
                        Device {i + 1}
                      </span>
                      <button
                        onClick={() => removeDeviceRow(i)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 13,
                          color: "var(--text-dim)",
                          lineHeight: 1,
                          padding: "0 2px",
                        }}
                      >
                        ×
                      </button>
                    </div>

                    <div
                      style={{
                        padding: 14,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 12,
                      }}
                    >
                      {/* Device type */}
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-dim)",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            fontWeight: 600,
                            marginBottom: 5,
                          }}
                        >
                          Device Type
                        </div>
                        <select
                          value={dev.deviceType || "box"}
                          onChange={(e) => setDeviceField(i, "deviceType", e.target.value)}
                          disabled={!dev.isNew && !!dev.existingId}
                          style={{
                            width: "100%",
                            background: !dev.isNew && dev.existingId ? "var(--surface-alt)" : "var(--surface)",
                            border: `1px solid var(--border)`,
                            borderRadius: 6,
                            padding: "7px 10px",
                            fontSize: 12,
                            color: "var(--text)",
                            fontFamily: "inherit",
                            outline: "none",
                            cursor: !dev.isNew && dev.existingId ? "default" : "pointer",
                            opacity: !dev.isNew && dev.existingId ? 0.7 : 1,
                          }}
                        >
                          <option value="box">Box (eBox device)</option>
                          <option value="self-srt-connect">Self-SRT-Connect (direct internet, IP only)</option>
                        </select>
                      </div>

                      {/* Existing device selector */}
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-dim)",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            fontWeight: 600,
                            marginBottom: 5,
                          }}
                        >
                          Select Existing Device
                        </div>
                        <DeviceCombobox
                          devices={(() => {
                            const unassigned = existingDevices.filter(d => !d.affiliate_id);
                            return unassigned.length > 0
                              ? unassigned
                              : existingDevices.filter(d => d.affiliate_id === form.id);
                          })()}
                          value={dev.existingId || ""}
                          onChange={(id) => selectExistingDevice(i, id)}
                        />
                      </div>

                      {/* MAC address — box devices only */}
                      {dev.deviceType !== "self-srt-connect" && (
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-dim)",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            fontWeight: 600,
                            marginBottom: 5,
                          }}
                        >
                          MAC Address
                        </div>
                        <input
                          value={dev.mac}
                          onChange={(e) =>
                            setDeviceField(i, "mac", e.target.value)
                          }
                          placeholder="AA:BB:CC:DD:EE:FF"
                          disabled={!dev.isNew && !!dev.existingId}
                          style={{
                            width: "100%",
                            background:
                              !dev.isNew && dev.existingId
                                ? "var(--surface-alt)"
                                : "var(--surface)",
                            border: `1px solid ${dev.mac && !validMAC(dev.mac) ? "var(--red)" : "var(--border)"}`,
                            borderRadius: 6,
                            padding: "7px 10px",
                            fontSize: 12,
                            color: "var(--text)",
                            fontFamily: "'IBM Plex Mono', monospace",
                            outline: "none",
                            boxSizing: "border-box",
                            opacity: !dev.isNew && dev.existingId ? 0.7 : 1,
                          }}
                        />
                        {dev.mac && !validMAC(dev.mac) && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--red)",
                              marginTop: 3,
                            }}
                          >
                            Invalid format
                          </div>
                        )}
                      </div>
                      )}

                      {/* Self-SRT-Connect — plain required IP field, no DHCP/static concept */}
                      {dev.deviceType === "self-srt-connect" && (
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-dim)",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            fontWeight: 600,
                            marginBottom: 5,
                          }}
                        >
                          IP Address *
                        </div>
                        <input
                          value={dev.staticIp || ""}
                          onChange={(e) => setDeviceField(i, "staticIp", e.target.value)}
                          placeholder="192.168.1.100"
                          disabled={!dev.isNew && !!dev.existingId}
                          style={{
                            width: "100%",
                            background: !dev.isNew && dev.existingId ? "var(--surface-alt)" : "var(--surface)",
                            border: `1px solid var(--border)`,
                            borderRadius: 6,
                            padding: "7px 10px",
                            fontSize: 12,
                            color: "var(--text)",
                            fontFamily: "'IBM Plex Mono', monospace",
                            outline: "none",
                            boxSizing: "border-box",
                            opacity: !dev.isNew && dev.existingId ? 0.7 : 1,
                          }}
                        />
                        <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 4 }}>
                          No box, no MAC — whitelisted by IP directly.
                        </div>
                      </div>
                      )}

                      {/* IP Type toggle — box devices only */}
                      {dev.deviceType !== "self-srt-connect" && (
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-dim)",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            fontWeight: 600,
                            marginBottom: 5,
                          }}
                        >
                          IP Type
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: 0,
                            border: `1px solid var(--border)`,
                            borderRadius: 6,
                            overflow: "hidden",
                          }}
                        >
                          {["dhcp", "static"].map((type) => (
                            <button
                              key={type}
                              onClick={() => setDeviceField(i, "ipType", type)}
                              style={{
                                flex: 1,
                                padding: "7px 0",
                                fontSize: 11,
                                fontWeight: dev.ipType === type ? 600 : 400,
                                background:
                                  dev.ipType === type
                                    ? "var(--ink)"
                                    : "var(--surface)",
                                color:
                                  dev.ipType === type
                                    ? "#fff"
                                    : "var(--text-sub)",
                                border: "none",
                                cursor: "pointer",
                                fontFamily: "inherit",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                transition: "all 0.12s",
                              }}
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                      </div>
                      )}

                      {/* Static IP input — only shown when static is selected (box only) */}
                      {dev.deviceType !== "self-srt-connect" && dev.ipType === "static" && (
                        <div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-dim)",
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              fontWeight: 600,
                              marginBottom: 5,
                            }}
                          >
                            Static IP Address
                          </div>
                          <input
                            value={dev.staticIp || ""}
                            onChange={(e) =>
                              setDeviceField(i, "staticIp", e.target.value)
                            }
                            placeholder="192.168.1.100"
                            style={{
                              width: "100%",
                              background: "var(--surface)",
                              border: `1px solid var(--border)`,
                              borderRadius: 6,
                              padding: "7px 9px",
                              fontSize: 12,
                              color: "var(--text)",
                              fontFamily: "'IBM Plex Mono', monospace",
                              outline: "none",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                      )}

                      {/* DHCP — shown read-only when dhcp is selected (box only) */}
                      {dev.deviceType !== "self-srt-connect" && dev.ipType === "dhcp" && (
                        <div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-dim)",
                              textTransform: "uppercase",
                              letterSpacing: "0.07em",
                              fontWeight: 600,
                              marginBottom: 5,
                            }}
                          >
                            IP Address
                          </div>
                          <div
                            style={{
                              padding: "7px 10px",
                              background: "var(--surface-alt)",
                              border: `1px solid var(--border)`,
                              borderRadius: 6,
                              fontSize: 12,
                              color: "var(--text-sub)",
                              fontFamily: "'IBM Plex Mono', monospace",
                            }}
                          >
                            {dev.dhcpIp || "Assigned on device boot"}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-dim)",
                              marginTop: 3,
                            }}
                          >
                            Auto-assigned via eBox /register API
                          </div>
                        </div>
                      )}

                      {/* Device model — box devices only */}
                      {dev.deviceType !== "self-srt-connect" && (
                      <div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-dim)",
                            textTransform: "uppercase",
                            letterSpacing: "0.07em",
                            fontWeight: 600,
                            marginBottom: 5,
                          }}
                        >
                          Device Model
                        </div>
                        <select
                          value={dev.model}
                          onChange={(e) =>
                            setDeviceField(i, "model", e.target.value)
                          }
                          disabled={!dev.isNew && !!dev.existingId}
                          style={{
                            width: "100%",
                            background:
                              !dev.isNew && dev.existingId
                                ? "var(--surface-alt)"
                                : "var(--surface)",
                            border: `1px solid var(--border)`,
                            borderRadius: 6,
                            padding: "7px 10px",
                            fontSize: 12,
                            color: "var(--text)",
                            fontFamily: "inherit",
                            outline: "none",
                            cursor:
                              !dev.isNew && dev.existingId
                                ? "default"
                                : "pointer",
                            opacity: !dev.isNew && dev.existingId ? 0.7 : 1,
                          }}
                        >
                          {deviceModels.length === 0 ? (
                            <option value="">No models in DB</option>
                          ) : (
                            deviceModels.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                      )}

                      {/* Channel assignment — full width row */}
                      <div style={{ gridColumn: "1/-1" }}>
                        <div style={{ fontSize:11, color:"var(--text-dim)", textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:600, marginBottom:8 }}>
                          Assign Channel{isMultiChannelDevice(dev) ? `s (up to ${maxChannelsForDevice(dev)})` : ""}
                        </div>

                        {isMultiChannelDevice(dev) ? (
                          /* ── multi-channel models: chip picker ── */
                          (() => {
                            const MAX_3568 = maxChannelsForDevice(dev);
                            const picked = Array.isArray(dev.channelIds) ? dev.channelIds : (dev.channelId ? [dev.channelId] : []);
                            const blockedInWizard = channelsPickedByType(i, dev.deviceType || "box");
                            const available = channels.filter(c => !picked.includes(c.id) && !blockedInWizard.includes(c.id));
                            const slotsLeft = MAX_3568 - picked.length;

                            return (
                              <div>
                                {picked.length > 0 && (
                                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:8 }}>
                                    {picked.map(chId => {
                                      const ch = channels.find(c => c.id === chId);
                                      return (
                                        <span key={chId} style={{ display:"inline-flex", alignItems:"center", gap:5, background:"var(--ink)", color:"#fff", borderRadius:5, padding:"4px 10px", fontSize:11, fontFamily:"'IBM Plex Mono', monospace" }}>
                                          {chId}{ch ? ` · ${ch.display_name}` : ""}
                                          <button onClick={() => setDeviceField(i, "channelIds", picked.filter(id => id !== chId))}
                                            style={{ background:"none", border:"none", color:"rgba(255,255,255,0.7)", cursor:"pointer", fontSize:13, lineHeight:1, padding:0 }}>×</button>
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                                {slotsLeft > 0 ? (
                                  <select
                                    value=""
                                    onChange={(e) => {
                                      if (!e.target.value) return;
                                      setDeviceField(i, "channelIds", [...picked, e.target.value]);
                                    }}
                                    style={{ width:"100%", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"7px 10px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
                                  >
                                    <option value="">— Add channel ({slotsLeft} slot{slotsLeft !== 1 ? "s" : ""} remaining) —</option>
                                    {available.map(ch => (
                                      <option key={ch.id} value={ch.id}>{ch.id} · {ch.display_name}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <div style={{ fontSize:11, color:"var(--text-dim)" }}>All {MAX_3568} slots filled.</div>
                                )}
                                <div style={{ fontSize:10, color:"var(--text-dim)", marginTop:5 }}>
                                  {dev.model} supports up to {MAX_3568} channels. A channel can appear once per device type — once across box devices and once across self-SRT devices — for the same affiliate.
                                </div>
                              </div>
                            );
                          })()
                        ) : (                        
                          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                          {channels.map((ch) => {
                            const blocked = isChannelBlocked(ch.id, i);
                            const inUseElsewhere = isChannelInUseElsewhere(ch.id);
                            const selected = dev.channelId === ch.id;
                            return (
                              <div
                                key={ch.id}
                                onClick={() => !blocked && setDeviceField(i, "channelId", selected ? "" : ch.id)}
                                title={blocked ? "Already assigned to another device in this wizard" : ""}
                                style={{ display:"flex", alignItems:"center", gap:7, padding:"6px 12px", borderRadius:6, border:`1.5px solid ${selected ? "var(--ink)" : "var(--border)"}`, background: selected ? "var(--ink)" : blocked ? "var(--surface-alt)" : "var(--surface)", cursor: blocked ? "not-allowed" : "pointer", opacity: blocked ? 0.45 : 1, transition:"all 0.12s" }}
                              >
                                <span style={{ fontSize:12, fontWeight: selected ? 600 : 400, color: selected ? "#fff" : blocked ? "var(--text-dim)" : "var(--ink)", fontFamily:"'IBM Plex Mono', monospace" }}>{ch.id}</span>
                                <span style={{ fontSize:11, color: selected ? "rgba(255,255,255,0.7)" : "var(--text-sub)" }}>{ch.display_name}</span>
                                {blocked ? (
                                  <span style={{ fontSize:10, color:"var(--red)", fontWeight:500 }}>In use</span>
                                ) : inUseElsewhere && !selected ? (
                                  <span style={{ fontSize:10, color:"var(--text-dim)", fontWeight:500 }}>In use</span>
                                ) : null}
                              </div>
                            );
                          })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add device button */}
            <button
              onClick={addDeviceRow}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "transparent",
                border: `1px dashed var(--border-strong)`,
                borderRadius: 7,
                padding: "9px 16px",
                color: "var(--text-sub)",
                cursor: "pointer",
                fontSize: 12,
                fontFamily: "inherit",
                width: "100%",
              }}
            >
              <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add Device
            </button>

            <div
              style={{
                marginTop: 18,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Btn onClick={goBack}>← Back</Btn>
              <Btn primary onClick={goNext}>
                Next →
              </Btn>
            </div>
          </div>
        )}

        {/* ── Step 4: Review & Confirm ───────────────────────────────────────── */}
        {step === 4 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Affiliate details */}
            <div
              style={{
                background: "var(--surface)",
                border: `1px solid var(--border)`,
                borderRadius: 9,
                padding: 20,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--text-dim)",
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  marginBottom: 12,
                }}
              >
                Affiliate Details
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                {[
                  ["ID", form.id],
                  ["Organization", form.org],
                  ["Contact", form.contact],
                  ["Email", form.email],
                  ["Region", form.region],
                  ["Timezone", form.tz],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 16px 8px 0",
                      borderBottom: `1px solid var(--border)`,
                    }}
                  >
                    <span style={{ fontSize: 11, color: "var(--text-sub)" }}>
                      {k}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: v ? "var(--ink)" : "var(--text-dim)",
                      }}
                    >
                      {v || "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Device allocations */}
            {form.devices.length > 0 ? (
              <div
                style={{
                  background: "var(--surface)",
                  border: `1px solid var(--border)`,
                  borderRadius: 9,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text-dim)",
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    padding: "12px 16px",
                    borderBottom: `1px solid var(--border)`,
                    background: "var(--surface-alt)",
                  }}
                >
                  Device Allocations — {form.devices.length} device
                  {form.devices.length !== 1 ? "s" : ""}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <TH>Device</TH>
                      <TH>MAC Address</TH>
                      <TH>IP Address</TH>
                      <TH>Type</TH>
                      <TH>Channel</TH>
                      <TH>SRT URL</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {form.devices.map((dev, i) => {
                      const ch = channels.find((c) => c.id === dev.channelId);
                      return (
                        <tr key={i}>
                          <TD>
                            <span
                              style={{ fontSize: 11, color: "var(--text-dim)" }}
                            >
                              {dev.isNew ? "New" : "Existing"}
                            </span>
                          </TD>
                          <TD mono>{dev.mac?.toUpperCase() || "—"}</TD>
                          <TD mono>
                            {dev.dhcpIp?.toUpperCase() ||
                              dev.staticIp?.toUpperCase()}
                          </TD>
                          <TD sub>{dev.deviceType === "self-srt-connect" ? "" : (dev.model || "—")}</TD>
                          <TD>
                            {dev.channelId ? (
                              <Pill label={dev.channelId} />
                            ) : (
                              <span
                                style={{
                                  fontSize: 12,
                                  color: "var(--text-dim)",
                                }}
                              >
                                —
                              </span>
                            )}
                          </TD>
                          <TD
                            mono
                            sub
                            style={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {ch?.master_srt_url || "—"}
                          </TD>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                style={{
                  background: "var(--surface-alt)",
                  border: `1px solid var(--border)`,
                  borderRadius: 9,
                  padding: "16px 20px",
                  fontSize: 12,
                  color: "var(--text-sub)",
                }}
              >
                No devices configured — can be assigned later from Device
                Management.
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Btn onClick={goBack}>← Back</Btn>
              <Btn primary onClick={handleCreate} disabled={submitting}>
                {submitting ? "Creating…" : "Create Affiliate"}
              </Btn>
            </div>
          </div>
        )}
      </>
    );

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
          paddingBottom: 14,
          borderBottom: `1px solid var(--border)`,
        }}
      >
        <h1 style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>
          Distributer Management
        </h1>
        <div style={{ display: "flex", gap: 8 }}>
          {!readOnly && affiliates.some((a) => a.status === "archived") && (
            <button
              onClick={handlePurge}
              disabled={purging}
              style={{
                background: "var(--surface)",
                border: `1px solid var(--border)`,
                borderRadius: 6,
                padding: "7px 12px",
                fontSize: 12,
                color: "var(--text-sub)",
                cursor: purging ? "not-allowed" : "pointer",
                fontFamily: "inherit",
              }}
            >
              {purging ? "Purging…" : "Purge Archived"}
            </button>
          )}
          {!readOnly && (
            <button
              onClick={() => { setShowBulkModal(true); setBulkResult(null); }}
              style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:7, padding:"7px 14px", fontSize:12, fontWeight:600, color:"var(--ink)", cursor:"pointer", fontFamily:"inherit", marginRight:6 }}
            >↑ Bulk Upload</button>
          )}
          {!readOnly && (
            <Btn primary onClick={() => setShowNew(true)}>
              + New Affiliate
            </Btn>
          )}
        </div>
      </div>

      {purgeResult && (
        <div
          style={{
            background: "var(--amber-bg)",
            border: `1px solid var(--amber-border)`,
            borderRadius: 7,
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: 12,
            color: "var(--amber)",
          }}
        >
          {purgeResult}
        </div>
      )}

      {error && (
        <div
          style={{
            background: "var(--red-bg)",
            border: `1px solid var(--red-border)`,
            borderRadius: 7,
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: 12,
            color: "var(--red)",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--text-dim)",
            padding: "40px 0",
            textAlign: "center",
          }}
        >
          Loading affiliates…
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
            <StatCard label="Total Affiliates" value={affiliates.length} />
            <StatCard
              label="Active"
              value={affiliates.filter((a) => a.status === "active").length}
            />
            <StatCard
              label="In-Active"
              value={affiliates.filter((a) => a.status === "inactive").length}
            />
            <StatCard
              label="Archived"
              value={affiliates.filter((a) => a.status === "archived").length}
            />
            <StatCard
              label="Total Devices"
              value={affiliates.reduce((s, a) => s + Number(a.devices || 0), 0)}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display:"flex", gap:6 }}>
              {["all", "active", "inactive", "archived"].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  style={{
                    background: filterStatus === s ? "var(--ink)" : "var(--surface)",
                    border: `1px solid ${filterStatus === s ? "var(--ink)" : "var(--border)"}`,
                    borderRadius: 5, padding: "5px 12px",
                    color: filterStatus === s ? "var(--ink-text)" : "var(--text-sub)",
                    cursor: "pointer", fontSize: 11,
                    fontWeight: filterStatus === s ? 600 : 400,
                    textTransform: "capitalize", fontFamily: "inherit",
                  }}
                >
                  {s === "all" ? "All Status" : s}
                </button>
              ))}

              <input
              value={affSearch}
              onChange={(e) => setAffSearch(e.target.value)}
              placeholder="Search by name, ID, region…"
              style={{ 
                background:"var(--surface)", 
                border:"1px solid var(--border)", 
                borderRadius:6, 
                padding:"5px 11px", 
                fontSize:12, 
                color:"var(--text)", 
                fontFamily:"inherit", 
                outline:"none", 
                // width:220, 
                flex: 1
              }}
            />
            </div>
          </div>       

          <TableWrap>
            <thead>
              <tr>
                <TH>Affiliate ID</TH>
                <TH>Organization</TH>
                <TH>Channels</TH>
                <TH>Devices</TH>
                <TH>Online</TH>
                <TH>Offline</TH>
                <TH>Status</TH>
                <TH>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    style={{
                      padding: "28px 14px",
                      textAlign: "center",
                      fontSize: 12,
                      color: "var(--text-dim)",
                    }}
                  >
                    No affiliates found.
                  </td>
                </tr>
              ) : (
                filtered.map((a) => (
                  <tr
                    key={a.id}
                    // onClick={() => setViewingId(a.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <TD mono>{a.id}</TD>
                    <TD>
                      <span style={{ fontWeight: 500 }}>{a.org_name}</span>
                      <br />
                      <span style={{ fontSize: 11, color: "var(--text-sub)" }}>
                        {a.contact_name}
                      </span>
                      {childCountByParent[a.id] > 0 && (
                        <div style={{ marginTop: 3 }}>
                          <span style={{ fontSize: 10, fontWeight: 600, color: "#0891B2", background: "#CFFAFE", border: "1px solid #67E8F9", borderRadius: 4, padding: "1px 6px" }}>
                            ▸ {childCountByParent[a.id]} affiliate{childCountByParent[a.id] !== 1 ? "s" : ""}
                          </span>
                        </div>
                      )}
                      {a.parent_id && (
                        <div style={{ marginTop: 3 }}>
                          <span style={{ fontSize: 10, fontWeight: 600, color: "#7C3AED", background: "#EDE9FE", border: "1px solid #C4B5FD", borderRadius: 4, padding: "1px 6px" }}>
                            ↳ Aff of: {affById[a.parent_id]?.org_name || a.parent_id}
                          </span>
                        </div>
                      )}
                    </TD>
                    <TD sub>{a.channels || 0}</TD>
                    <TD sub>{a.devices || 0}</TD>
                    <TD>
                      <span
                        style={{
                          color: "var(--green)",
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      >
                        {a.online || 0}
                      </span>
                    </TD>
                    <TD>
                      <span
                        style={{
                          color:
                            Number(a.offline) > 0
                              ? "var(--red)"
                              : "var(--text-dim)",
                          fontWeight: Number(a.offline) > 0 ? 600 : 400,
                          fontSize: 12,
                        }}
                      >
                        {a.offline || 0}
                      </span>
                    </TD>
                    <TD>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {readOnly ? (
                          <Badge status={a.status} />
                        ) : (
                          <>
                            <StatusToggle
                              id={a.id}
                              currentStatus={a.status}
                              onConfirm={handleStatusChange}
                            />
                            <Badge status={a.status} />
                          </>
                        )}
                      </div>
                    </TD>

                    <TD>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Btn small onClick={() => setViewingId(a.id)}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            <Eye size={13} /> View
                          </span>
                        </Btn>
                        {!readOnly && (
                          <>
                            <Btn
                              small
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingId(a.id);
                              }}
                            >
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                <Pencil size={13} /> Edit
                              </span>
                            </Btn>
                            {/* Archive only shown for inactive affiliates */}
                            {a.status === "inactive" && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingId(a.id);
                                }}
                                style={{
                                  background: "var(--amber-bg)",
                                  border: `1px solid var(--amber-border)`,
                                  borderRadius: 5,
                                  padding: "4px 10px",
                                  fontSize: 11,
                                  color: "var(--amber)",
                                  cursor: "pointer",
                                  fontFamily: "inherit",
                                }}
                              >
                                Archive
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </TD>
                  </tr>
                ))
              )}
            </tbody>
          </TableWrap>
        </>
      )}

      {deletingId && (
        <DeleteConfirm
          id={deletingId}
          label="Affiliate"
          onConfirm={handleDelete}
          onClose={() => setDeletingId(null)}
        />
      )}

      {viewingId && (
        <AffiliateViewer
          affiliateId={viewingId}
          onClose={() => setViewingId(null)}
        />
      )}
    <>
      <input
        ref={bulkInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display:"none" }}
        onChange={(e) => handleBulkUpload(e.target.files?.[0])}
      />

      {showBulkModal && (
        <div onClick={() => { setShowBulkModal(false); setBulkResult(null); }}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:12, padding:"28px", width:520, maxWidth:"92vw", boxShadow:"0 8px 40px rgba(0,0,0,0.15)", fontFamily:"inherit" }}>
            {/* Header */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:"var(--ink)" }}>Bulk Upload Affiliates</div>
                <div style={{ fontSize:12, color:"var(--text-sub)", marginTop:3 }}>Import from eBox Worksheet (.xlsx)</div>
              </div>
              <button onClick={() => { setShowBulkModal(false); setBulkResult(null); }}
                style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-dim)", fontSize:20, padding:"2px 6px" }}>×</button>
            </div>

            {/* Column info */}
            <div style={{ background:"var(--surface-alt)", border:"1px solid var(--border)", borderRadius:8, padding:"12px 14px", marginBottom:14 }}>
              <div style={{ fontSize:12, fontWeight:600, color:"var(--ink)", marginBottom:8 }}>Reads these columns from the eBox worksheet:</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px 12px" }}>
                {[
                  ["Customer code",                 "ID seed (grouped by Affiliate Name)"],
                  ["Affiliate Name",                "Operator / org name"],
                  ["Affiliate Organization Name",   "Preferred org name (overrides above)"],
                  ["Affiliate Contact Name",         "Contact person"],
                  ["Affiliate Contact Email",        "Contact email"],
                  ["Affiliate Contact Phone Number", "Phone"],
                  ["Affiliate Region",               "Region"],
                  ["E-box MAC Addresses",            "Device MAC (hex or colon-sep)"],
                  ["Channel Names (SD/HD)",          "Channel assignment"],
                  ["IP Type (Static/Dynamic)",       "DHCP or static IP"],
                  ["IP Address (if Static)",         "Static IP value"],
                  ["E-box Location",                 "With Operator / In Hand"],
                ].map(([col,use]) => (
                  <div key={col} style={{ fontSize:11, display:"flex", gap:4, alignItems:"baseline" }}>
                    <span style={{ fontFamily:"monospace", color:"var(--text)", whiteSpace:"nowrap" }}>{col}</span>
                    <span style={{ color:"var(--text-dim)", flexShrink:0 }}> → {use}</span>
                  </div>
                ))}
              </div>
              {/* <div style={{ fontSize:11, color:"var(--text-dim)", marginTop:10, borderTop:"1px solid var(--border)", paddingTop:8 }}>
                <strong>Grouping:</strong> rows with the same <em>Affiliate Name</em> are merged — so an operator with multiple Customer Codes (e.g. 1214199TEN + 1214199SPN) becomes one record with all devices attached.<br/>
                <strong>Channels:</strong> Only the 12 active DB channels are recognised. Devices with any other channel name are skipped entirely. If all of an affiliate's devices are skipped, that affiliate is not created either.
              </div> */}
            </div>

            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setBulkDragOver(true); }}
              onDragLeave={() => setBulkDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setBulkDragOver(false); handleBulkUpload(e.dataTransfer.files?.[0]); }}
              onClick={() => bulkInputRef.current?.click()}
              style={{ border:`2px dashed ${bulkDragOver ? "var(--ink)" : "var(--border)"}`, borderRadius:10, padding:"28px 20px", textAlign:"center", cursor:"pointer", background: bulkDragOver ? "var(--surface-alt)" : "transparent", transition:"all 0.15s", marginBottom:14 }}
            >
              <div style={{ fontSize:28, marginBottom:8 }}>📊</div>
              <div style={{ fontSize:13, fontWeight:600, color:"var(--text)", marginBottom:4 }}>{bulkDragOver ? "Drop to upload" : "Drag & drop your worksheet here"}</div>
              <div style={{ fontSize:12, color:"var(--text-dim)", marginBottom:14 }}>or</div>
              <button
                onClick={(e) => { e.stopPropagation(); bulkInputRef.current?.click(); }}
                disabled={bulkUploading}
                style={{ background: bulkUploading ? "var(--surface-alt)" : "var(--ink)", border:"none", borderRadius:7, padding:"8px 20px", fontSize:12, fontWeight:600, color: bulkUploading ? "var(--text-dim)" : "#fff", cursor: bulkUploading ? "not-allowed" : "pointer", fontFamily:"inherit", display:"inline-flex", alignItems:"center", gap:7 }}
              >
                {bulkUploading
                  ? <><span style={{ display:"inline-block", width:12, height:12, border:"2px solid var(--border)", borderTopColor:"var(--text-sub)", borderRadius:"50%", animation:"bulk-spin 0.7s linear infinite" }} />Processing…</>
                  : "Browse file"}
              </button>
              <div style={{ fontSize:11, color:"var(--text-dim)", marginTop:12 }}>.xlsx · .xls · .csv</div>
            </div>

            {/* Results */}
            {bulkResult && (() => {
              const skippedAffs = (bulkResult.skipped || []).filter(s => !s.type);
              const skippedDevs = (bulkResult.skipped || []).filter(s => s.type === 'device');
              const hasWarnings = (skippedAffs.length + skippedDevs.length + (bulkResult.skippedAffiliates?.length||0) + (bulkResult.channelFlags?.length||0) + (bulkResult.errors?.length||0)) > 0;
              return (
              <div style={{ borderRadius:8, overflow:"hidden", border:"1px solid var(--border)" }}>
                {(bulkResult.affiliates?.length > 0 || bulkResult.devices?.length > 0) && (
                  <div style={{ background:"var(--green-bg,#f0fdf4)", borderBottom: hasWarnings ? "1px solid var(--border)" : "none", padding:"12px 14px" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"var(--green,#16a34a)", marginBottom:6 }}>
                      ✓ {bulkResult.affiliates?.length||0} affiliate{(bulkResult.affiliates?.length||0)!==1?"s":""} · {bulkResult.devices?.length||0} device{(bulkResult.devices?.length||0)!==1?"s":""} created
                    </div>
                    {bulkResult.affiliates?.map((a,i) => (
                      <div key={i} style={{ fontSize:11, color:"var(--green,#16a34a)", marginBottom:2, fontFamily:"monospace" }}>{a.id} — {a.name}</div>
                    ))}
                  </div>
                )}
                {skippedAffs.length > 0 && (
                  <div style={{ background:"var(--amber-bg,#fffbeb)", borderBottom:(skippedDevs.length+(bulkResult.skippedAffiliates?.length||0)+(bulkResult.channelFlags?.length||0)+(bulkResult.errors?.length||0))>0?"1px solid var(--border)":"none", padding:"12px 14px" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"var(--amber,#d97706)", marginBottom:4 }}>
                      ↷ {skippedAffs.length} affiliate{skippedAffs.length!==1?"s":""} already exist — skipped
                    </div>
                    {skippedAffs.map((a,i) => <div key={i} style={{ fontSize:11, color:"var(--amber,#d97706)" }}>{a.name}</div>)}
                  </div>
                )}
                {skippedDevs.length > 0 && (
                  <div style={{ background:"var(--amber-bg,#fffbeb)", borderBottom:((bulkResult.skippedAffiliates?.length||0)+(bulkResult.channelFlags?.length||0)+(bulkResult.errors?.length||0))>0?"1px solid var(--border)":"none", padding:"12px 14px" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"var(--amber,#d97706)", marginBottom:4 }}>
                      ↷ {skippedDevs.length} device{skippedDevs.length!==1?"s":""} already registered — skipped
                    </div>
                    {skippedDevs.map((d,i) => <div key={i} style={{ fontSize:11, color:"var(--amber,#d97706)", fontFamily:"monospace" }}>{d.name}</div>)}
                  </div>
                )}
                {bulkResult.skippedAffiliates?.length > 0 && (
                  <div style={{ background:"var(--amber-bg,#fffbeb)", borderBottom:((bulkResult.channelFlags?.length||0)+(bulkResult.errors?.length||0))>0?"1px solid var(--border)":"none", padding:"12px 14px" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"var(--amber,#d97706)", marginBottom:6 }}>
                      ⊘ {bulkResult.skippedAffiliates.length} affiliate{bulkResult.skippedAffiliates.length!==1?"s":""} not created — unrecognised channels
                    </div>
                    {bulkResult.skippedAffiliates.map((a,i) => (
                      <div key={i} style={{ fontSize:11, color:"var(--amber,#d97706)", marginBottom:2 }}>
                        <strong>{a.name}</strong> — {a.reason}
                      </div>
                    ))}
                  </div>
                )}
                {bulkResult.channelFlags?.length > 0 && (
                  <div style={{ background:"var(--amber-bg,#fffbeb)", borderBottom:(bulkResult.errors?.length||0)>0?"1px solid var(--border)":"none", padding:"12px 14px" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"var(--amber,#d97706)", marginBottom:6 }}>
                      ⚠ {bulkResult.channelFlags.length} unrecognised channel name{bulkResult.channelFlags.length!==1?"s":""} — not in the 12 active channels
                    </div>
                    {bulkResult.channelFlags.map((c,i) => (
                      <div key={i} style={{ fontSize:11, color:"var(--amber,#d97706)", marginBottom:2 }}>
                        <strong style={{ fontFamily:"monospace" }}>{c.name}</strong>
                        {c.affiliate ? <span style={{ color:"var(--text-dim)" }}> (from {c.affiliate})</span> : null}
                      </div>
                    ))}
                  </div>
                )}
                {bulkResult.errors?.length > 0 && (
                  <div style={{ background:"var(--red-bg,#fef2f2)", padding:"12px 14px" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"var(--red,#dc2626)", marginBottom:6 }}>✕ {bulkResult.errors.length} failed</div>
                    {bulkResult.errors.map((e,i) => <div key={i} style={{ fontSize:11, color:"var(--red,#dc2626)", marginBottom:2 }}><strong>{e.name}</strong> — {e.message}</div>)}
                  </div>
                )}
              </div>
              );
            })()}
            <style>{`@keyframes bulk-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      )}
    </>
    </>
  );
}